import { useState } from "react";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import EntityStateRelationSection from "@/components/onboarding/shared/EntityStateRelationSection";
import EUServicesSection from "@/components/onboarding/shared/EUServicesSection";
import type {
  EntityData,
  RelatieStatRomanData,
  ReprezentantRomania,
  SituatieServiciiUEData,
} from "@/types/entity";

type Step2AdditionalSectionsProps = {
  entityDataForSections: Partial<EntityData>;
  handlers: {
    handleRelatieFieldChange: (
      field: keyof RelatieStatRomanData,
      value: boolean | null
    ) => void;
    handleReprezentantChange: (
      field: keyof ReprezentantRomania,
      value: string
    ) => void;
    handleServiciiFieldChange: (
      field: keyof SituatieServiciiUEData,
      value: boolean | null
    ) => void;
    handleStatesChange: (value: string) => void;
    handleAddressesChange: (value: string) => void;
  };
  sectionErrors: Record<string, string>;
};

export function Step2AdditionalSections({
  entityDataForSections,
  handlers,
  sectionErrors,
}: Step2AdditionalSectionsProps) {
  const [relatieOpen, setRelatieOpen] = useState(false);
  const [serviciiOpen, setServiciiOpen] = useState(false);
  const errorCount = Object.keys(sectionErrors).length;

  const relatieErrorCount = Object.keys(sectionErrors).filter((k) =>
    k.startsWith("relatie_stat_roman.")
  ).length;
  const serviciiErrorCount = Object.keys(sectionErrors).filter((k) =>
    k.startsWith("situatie_servicii_ue.")
  ).length;

  return (
    <>
      <Collapsible open={relatieOpen} onOpenChange={setRelatieOpen}>
        <Card className={cn(relatieErrorCount > 0 && "border-ds-red-500/20 bg-ds-red-500/5 transition-colors")}>
          <CollapsibleTrigger asChild>
            <CardHeader className={cn("p-3 md:p-4 cursor-pointer hover:bg-muted/50 transition-colors rounded-t-lg", relatieErrorCount > 0 && "bg-ds-red-50")}>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base md:text-lg">
                    Relația cu Statul Român
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm">
                    Informații despre stabilirea și activitatea entității în
                    România
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {relatieErrorCount > 0 && (
                    <span className="text-xs text-destructive font-medium">
                      {relatieErrorCount}{" "}
                      {relatieErrorCount === 1 ? "eroare" : "erori"}
                    </span>
                  )}
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${relatieOpen ? "rotate-180" : ""}`}
                  />
                </div>
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="p-3 pt-0 md:p-4 md:pt-0">
              <EntityStateRelationSection
                entityData={entityDataForSections}
                onFieldChange={handlers.handleRelatieFieldChange}
                onReprezentantChange={handlers.handleReprezentantChange}
                fieldErrors={sectionErrors}
              />
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <Collapsible open={serviciiOpen} onOpenChange={setServiciiOpen}>
        <Card className={cn(serviciiErrorCount > 0 && "border-ds-red-500/20 bg-ds-red-500/5 transition-colors")}>
          <CollapsibleTrigger asChild>
            <CardHeader className={cn("p-3 md:p-4 cursor-pointer hover:bg-muted/50 transition-colors rounded-t-lg", serviciiErrorCount > 0 && "bg-ds-red-50")}>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base md:text-lg">
                    Servicii în UE
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm">
                    Informații despre furnizarea de servicii în România și
                    statele membre UE
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {serviciiErrorCount > 0 && (
                    <span className="text-xs text-destructive font-medium">
                      {serviciiErrorCount}{" "}
                      {serviciiErrorCount === 1 ? "eroare" : "erori"}
                    </span>
                  )}
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${serviciiOpen ? "rotate-180" : ""}`}
                  />
                </div>
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="p-3 pt-0 md:p-4 md:pt-0">
              <EUServicesSection
                entityData={entityDataForSections}
                onFieldChange={handlers.handleServiciiFieldChange}
                onStatesChange={handlers.handleStatesChange}
                onAddressesChange={handlers.handleAddressesChange}
                fieldErrors={sectionErrors}
              />
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {errorCount > 0 && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
          <p className="text-sm text-destructive">
            Există {errorCount} {errorCount === 1 ? "eroare" : "erori"} de
            validare. Vă rugăm să corectați câmpurile marcate.
          </p>
        </div>
      )}
    </>
  );
}
